import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MapPin, Check, Star } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Breadcrumb from "@/components/site/Breadcrumb";
import Brands from "@/components/site/Brands";
import Realisations from "@/components/site/Realisations";
import EmergencyBanner from "@/components/site/EmergencyBanner";
import ContactForm from "@/components/site/ContactForm";
import MapCard from "@/components/site/MapCard";
import { Reveal } from "@/components/site/Reveal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CITIES, SERVICES, COMPANY, buildCityContent, CITY_FAQS } from "@/data/content";
import { setSeo, setJsonLd } from "@/lib/seo";

const SITE = "https://serrurier-lagny-sur-marne.com";

export default function CityPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (city) {
      setSeo({
        title: `Serrurier ${city.name} (${city.cp}) — 24h/24 • ${COMPANY.phoneDisplay}`,
        description: `☎ ${COMPANY.phoneDisplay} — Serrurier à ${city.name} (${city.cp}) : dépannage urgence 24h/24, ouverture de porte sans dégât, changement de serrure, blindage, coffre-fort. Intervention en 30 min. Devis gratuit, artisan local.`,
        canonical: `${SITE}/zone/${city.slug}`,
      });
      setJsonLd({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Locksmith",
            name: `Serrurier ${city.name}`,
            telephone: COMPANY.phoneTel,
            url: `${SITE}/zone/${city.slug}`,
            priceRange: "€€",
            address: {
              "@type": "PostalAddress",
              streetAddress: "26 Rue Freycinet",
              addressLocality: "Lagny-sur-Marne",
              postalCode: "77400",
              addressRegion: "Seine-et-Marne",
              addressCountry: "FR",
            },
            areaServed: {
              "@type": "City",
              name: city.name,
              postalCode: city.cp,
            },
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [
                  "Monday", "Tuesday", "Wednesday", "Thursday",
                  "Friday", "Saturday", "Sunday",
                ],
                opens: "00:00",
                closes: "23:59",
              },
            ],
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(COMPANY.rating),
              reviewCount: String(COMPANY.reviewsCount),
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Zones d'intervention", item: `${SITE}/#zones` },
              {
                "@type": "ListItem",
                position: 3,
                name: `Serrurier ${city.name}`,
                item: `${SITE}/zone/${city.slug}`,
              },
            ],
          },
          ...(CITY_FAQS[city.slug]
            ? [
                {
                  "@type": "FAQPage",
                  mainEntity: CITY_FAQS[city.slug].map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                },
              ]
            : []),
        ],
      });
    }
    window.scrollTo(0, 0);
  }, [city]);

  if (!city) return <Navigate to="/" replace />;

  return (
    <div className="App relative">
      <Navbar />
      <main>
        {/* Hero with city banner image */}
        <section className="relative bg-brand-mist pt-28 pb-16 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
            <Breadcrumb
              items={[
                { label: "Accueil", to: "/" },
                { label: "Zones d'intervention", to: "/#zones" },
                { label: `Serrurier ${city.name}` },
              ]}
            />

            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4 text-brand-gold">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                  <span className="text-xs uppercase tracking-[0.16em] text-slate-500 ml-2 font-medium">
                    {COMPANY.rating}/5 · {COMPANY.reviewsCount} avis
                  </span>
                </div>
                <h1 className="font-display font-semibold text-brand-navy tracking-tight leading-[1.02] text-5xl sm:text-6xl md:text-7xl">
                  Serrurier{" "}
                  <span className="block text-brand-golddark">{city.name}</span>
                </h1>
                <div className="mt-5 flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4 text-brand-gold" />
                  <span className="text-sm uppercase tracking-[0.12em] font-medium">
                    {city.name} · {city.cp} · Seine-et-Marne (77)
                  </span>
                </div>
                <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
                  {city.intro}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="city-call-btn"
                    className="inline-flex items-center justify-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 active:scale-[0.97] shadow-lg shadow-brand-gold/20"
                  >
                    <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
                  </a>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy2 text-white px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300"
                  >
                    Devis gratuit
                  </a>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl overflow-hidden ring-1 ring-brand-navy/10 shadow-[0_50px_120px_-40px_rgba(10,30,63,0.5)]"
              >
                <img
                  src={`/villes/${city.slug}.jpg`}
                  alt={`Serrurier ${city.name} — dépannage 24h/24`}
                  className="w-full h-auto block"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services list for the city */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal className="mb-10">
              <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-brand-navy">
                Nos services de serrurerie à {city.name}
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                return (
                  <Reveal key={s.slug} delay={(i % 3) * 0.05}>
                    <Link
                      to={`/services/${s.slug}`}
                      data-testid={`city-service-${s.slug}`}
                      className="flex items-start gap-4 rounded-2xl border border-brand-line bg-white p-6 hover:shadow-lg hover:shadow-brand-navy/5 hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
                    >
                      <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand-navy/5 text-brand-navy shrink-0">
                        <Icon className="w-5 h-5" />
                      </span>
                      <div>
                        <div className="font-display font-medium text-lg text-brand-navy">
                          {s.title}
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          {s.desc}
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* SEO editorial article (~1500 mots) */}
        <section
          data-testid="city-seo-article"
          className="py-20 md:py-28 bg-brand-mist"
        >
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <article className="space-y-12">
              {buildCityContent(city).map((block, i) => (
                <Reveal key={i} as="div">
                  <h2 className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-brand-navy leading-tight mb-4">
                    {block.h2}
                  </h2>
                  {block.p.map((para, j) => (
                    <p
                      key={j}
                      className="text-slate-600 leading-relaxed text-base md:text-lg mb-4"
                    >
                      {para}
                    </p>
                  ))}
                  {block.list && (
                    <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                      {block.list.map((item) => (
                        <li key={item} className="flex items-center gap-3 text-brand-navy">
                          <Check className="w-4 h-4 text-brand-gold shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </Reveal>
              ))}

              <Reveal>
                <div className="rounded-2xl bg-brand-navy p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div>
                    <div className="font-display font-semibold text-2xl text-white">
                      Besoin d&apos;un serrurier à {city.name} ?
                    </div>
                    <p className="text-white/70 mt-1">
                      Intervention rapide, devis gratuit, 24h/24 et 7j/7.
                    </p>
                  </div>
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="city-article-call"
                    className="inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 shrink-0"
                  >
                    <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
                  </a>
                </div>
              </Reveal>
            </article>
          </div>
        </section>

        {/* FAQ localisée */}
        {CITY_FAQS[city.slug] && (
          <section className="py-20 md:py-24 bg-white" data-testid="city-faq">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
              <Reveal className="mb-10">
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
                  <span className="w-8 h-px bg-brand-gold" /> Questions fréquentes
                </span>
                <h2 className="mt-4 font-display font-semibold text-3xl md:text-4xl tracking-tight text-brand-navy">
                  Serrurier à {city.name} : vos questions
                </h2>
              </Reveal>
              <Accordion type="single" collapsible className="w-full">
                {CITY_FAQS[city.slug].map((f, i) => (
                  <AccordionItem key={i} value={`cf-${i}`} className="border-b border-brand-line">
                    <AccordionTrigger
                      data-testid={`city-faq-trigger-${i}`}
                      className="text-left font-display font-medium text-base md:text-lg tracking-tight text-brand-navy hover:text-brand-golddark hover:no-underline py-5"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* Maillage interne : autres villes */}
        <section className="py-16 bg-brand-mist border-t border-brand-line">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <h2 className="font-display font-semibold text-xl text-brand-navy mb-5">
              Nous intervenons également à
            </h2>
            <div className="flex flex-wrap gap-3">
              {CITIES.filter((c) => c.slug !== city.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/zone/${c.slug}`}
                  data-testid={`city-link-${c.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-navy hover:border-brand-gold hover:text-brand-golddark transition-colors duration-300"
                >
                  <MapPin className="w-3.5 h-3.5 text-brand-gold" />
                  Serrurier {c.name} ({c.cp})
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Brands />
        <Realisations citySlug={city.slug} cityName={city.name} />
        <MapCard cityName={city.name} />
        <ContactForm defaultCity={city.name} />
      </main>
      <Footer />
      <EmergencyBanner />
    </div>
  );
}
